import { useWeb3React } from '@web3-react/core';
import { formatEther, formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { useState, ReactNode, useReducer, useMemo } from 'react';
import AssetInput from '../../../../../components/AssetInput';
import PercentageSelector from '../../../../../components/PercentageSelector';
import { config } from '../../../../../config/config';
import {
    OrderBookType,
    Tokens,
    INSUFFICIENT_BALANCE,
    Validators,
    OrderDataTypes,
    EnabledChains,
} from '../../../../../config/constants';
import { AddMarginToPositionParamsStruct } from '../../../../../config/contracts/typechain/ITradeManager';
import useModalState from '../../../../../context/modal/useModalState';
import { formatUSD } from '../../../../../helpers';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import useTokenDetails from '../../../../../hooks/web3/useTokenDetails';
import useUserBalance from '../../../../../hooks/web3/useUserBalance';
import { preventOverflow } from '../../../../../web3/utils';
import LimitPriceDropdown from '../../LimitPriceDropdown';
import calculateBankruptcyPrice from '../../../../../helpers/calculations/calculateBankruptcyPrice';
import DepositPreview from './DepositPreview';
import { UserOrder } from '../../../../../hooks/web3/trade/useUserOpenOrders';
import useCancelOrder from '../../../../../hooks/web3/trade/useCancelOrder';
import { EditPositionType } from '..';
import useCreateOrder from '../../../../../hooks/web3/trade/useCreateOrder';
import useApproveToken from '../../../../../hooks/web3/useApproveToken';

export type EditPositionDepositProps = {
    children(confirm: {
        confirm: () => Promise<void>;
        validators: Validators[];
        isApproved: boolean;
        approve: (approveAmount: string) => Promise<void>;
    }): ReactNode;
    setOrderCancelled: React.Dispatch<React.SetStateAction<boolean>>;
    orderCancelled: boolean;
};

const EditPositionDeposit: React.FC<EditPositionDepositProps> = ({
    children,
    setOrderCancelled,
    orderCancelled,
}) => {
    const { modalData } = useModalState();
    const { chainId } = useWeb3React();

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition &&
        'orderData' in userPosition &&
        modalData.data.defaultEditTab === EditPositionType.DEPOSIT
            ? userPosition
            : undefined;

    const userBalance = useUserBalance(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );
    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const cancel = useCancelOrder();
    const createOrder = useCreateOrder();

    const [amount, setAmount] = useState(
        userOrder?.orderData
            ? formatUnits(
                  (
                      userOrder.orderData as OrderDataTypes<AddMarginToPositionParamsStruct>
                  ).params.addedMargin.toString(),
                  decimals
              )
            : ''
    );

    const [isLimitOrder, toggleIsLimitOrder] = useReducer(
        (prev) => !prev,
        !!userOrder?.limitPrice
    );
    const [limitPrice, setLimitPrice] = useState(userOrder?.limitPrice || '');
    const [withExpiryDate, toggleWithExpiryDate] = useReducer(
        (prev) => !prev,
        !!userOrder?.orderData.constraints.deadline
    );
    const [expiryDate, setExpiryDate] = useState<Date | undefined>(
        userOrder
            ? new Date(Number(userOrder.orderData.constraints.deadline) * 1000)
            : undefined
    );

    const { isApproved, approve } = useApproveToken(
        config.addresses[chainId as EnabledChains[number]]?.collateral,
        config.addresses[chainId as EnabledChains[number]]?.tradeManager,
        amount
    );

    const maxDepositAmount = useMemo(
        () =>
            parseUnits(userPosition?.volume || '0', decimals)
                .mul(parseUnits('1', decimals))
                .div(parseUnits('1.1', decimals))
                .sub(parseUnits(userPosition?.margin || '0', decimals)),
        [userPosition?.volume, userPosition?.margin, decimals]
    );

    const validators: Validators[] = [
        {
            errorCondition: !amount,
            message: 'Amount is required!',
        },
        {
            errorCondition: parseUnits(amount || '0', decimals).gt(userBalance || '0'),
            message: INSUFFICIENT_BALANCE,
        },
        {
            errorCondition: parseUnits(amount || '0', decimals).gt(
                maxDepositAmount || '0'
            ),
            message: `Maximum Collateral is ${formatUSD(
                formatUnits(maxDepositAmount || '0', decimals),
                { currency: '', minDecimals: 2 }
            )} USDC!`,
        },
        {
            errorCondition:
                isLimitOrder && (!limitPrice || parseEther(limitPrice).isZero()),
            message: 'Limit Price is required!',
        },
        {
            errorCondition: withExpiryDate && !expiryDate,
            message: 'Expiry Date is required!',
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setAmount(preventOverflow(value, decimals));
    };

    const handleMax = () => {
        setAmount(
            formatUSD(formatUnits(maxDepositAmount, decimals) || '0', {
                useGrouping: false,
                currency: '',
                maxDecimals: decimals,
            })
        );
    };

    const getBankruptcyPrice = () => {
        const newCollateral = parseEther(userPosition?.margin || '0').add(
            parseEther(amount || '0')
        );

        const newLeverage = parseEther(userPosition?.volume || '0')
            .mul(parseEther('1'))
            .div(newCollateral);

        return calculateBankruptcyPrice(
            userPosition?.entryPrice,
            userPosition?.isShort,
            formatEther(newLeverage)
        );
    };

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');

        if (userOrder && !orderCancelled) {
            await cancel(userOrder.orderId, userOrder.orderType, () =>
                setOrderCancelled(true)
            );
            return;
        }

        await createOrder(
            OrderBookType.ADD_MARGIN,
            {
                positionId: userPosition.id,
                tradePair: userPosition.tradePair.address,
                addedMargin: parseUnits(amount, decimals).toString(),
            },
            'AddMarginToPositionOrder',
            userPosition.isShort,
            getBankruptcyPrice,

            {
                expiryDate: withExpiryDate ? expiryDate : undefined,
                limitPrice: isLimitOrder ? limitPrice : undefined,
                isMarketOrder: !limitPrice ? true : undefined,
                tradePair: userPosition.tradePair,
                userPosition,
            }
        );
    };

    return (
        <>
            <span className='position-type-text'>
                Add collateral to lower your leverage
            </span>
            <div className='collateral-container'>
                <AssetInput
                    label='Collateral'
                    sideLabel={
                        <>
                            Balance:{' '}
                            {formatUSD(formatUnits(userBalance || '0', decimals), {
                                currency: '',
                            })}{' '}
                            USDC
                        </>
                    }
                    name='amount'
                    value={amount}
                    onChange={handleChange}
                    asset={Tokens.USDC}
                    onMax={handleMax}
                    disabled={!!userOrder && !orderCancelled}
                />
                <PercentageSelector
                    currentValue={amount}
                    totalValue={formatUnits(maxDepositAmount || '0', decimals)}
                    handleChange={(value) =>
                        userOrder && !orderCancelled
                            ? undefined
                            : setAmount(preventOverflow(value, decimals))
                    }
                    decimals={decimals}
                    disableButtons={!!userOrder && !orderCancelled}
                />
            </div>
            <LimitPriceDropdown
                selectedTradePair={userPosition?.tradePair}
                limitPriceChecked={isLimitOrder}
                toggleLimitPrice={toggleIsLimitOrder}
                orderExpiryChecked={withExpiryDate}
                toggleOrderExpiry={toggleWithExpiryDate}
                amount={limitPrice}
                onChange={(value) => setLimitPrice(value)}
                date={expiryDate}
                handleDateChange={setExpiryDate}
                disabled={!!userOrder && !orderCancelled}
            />
            <DepositPreview amount={amount} userPosition={userPosition} />
            {children({ confirm, validators, isApproved, approve })}
        </>
    );
};

export default EditPositionDeposit;
